import { useMemo, VFC } from 'react';

import ConnectedTagSelect from 'dashboard/components/ConnectedTagSelect';
import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { taggableObjectsFor, UsableObjectConfig } from 'dashboard/lib/workflows/objects';
import { WorkflowError } from 'dashboard/models/workflow';
import { WorkflowActionTagObjectV1 } from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';

interface ParamsTagObjectV1Props {
  editable: boolean;
  errors: WorkflowError[];
  focusStep: (token: string) => void;
  params: WorkflowActionTagObjectV1['params'];
  usableObjects: UsableObjectConfig[];
  onMenuClose: () => void;
  updateActionParams: (newParams: WorkflowActionTagObjectV1['params']) => void;
}

const ParamsTagObjectV1: VFC<ParamsTagObjectV1Props> = ({
  editable,
  errors,
  focusStep,
  params,
  usableObjects,
  onMenuClose,
  updateActionParams,
}) => {
  const possibleObjects = useMemo(() => taggableObjectsFor(usableObjects), [usableObjects]);

  const selectObject = (object: WorkflowActionTagObjectV1['params']['object']) => {
    updateActionParams({
      ...params,
      object,
    });
  };

  const onChange = async (tagIds: string[]) => {
    updateActionParams({
      ...params,
      // This needs to be backwards compatible as previous versions of this action
      // used to only take a string
      ...(tagIds.length === 1 ? { ['tag-id']: tagIds[0] } : { ['tag-id']: tagIds }),
    });
  };

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionTagObjectV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel labelText='Object' htmlFor='params-tag-object__object-input' />
      <ReactSelect
        value={params.object}
        options={possibleObjects}
        onChange={selectObject}
        id='params-tag-object__object'
        disabled={!editable}
        hasError={hasErrorFor('object')}
        onFocusOption={focusStep}
        onMenuClose={onMenuClose}
      />
      {renderErrorMessageFor('object')}
      <Spacer height={2} />
      <ParamLabel labelText='Tag' htmlFor='params-tag-object__tag-input' />
      <ConnectedTagSelect
        size='md'
        placeholder='To create a new tag start typing'
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        value={Array.isArray(params['tag-id']) ? params['tag-id'] : [params['tag-id']]}
        tagScope='all'
        onChange={onChange}
        id='params-tag-object__tag'
        disabled={!editable}
        hasError={hasErrorFor('tag-id')}
      />
      {renderErrorMessageFor('tag-id')}
    </>
  );
};

export default ParamsTagObjectV1;
